// Dev debug overlay (spec §11), toggled with backtick. Polls stats on a timer
// while visible so it never causes board redraws itself.
import type { Editor } from '../editor.ts';
import { h, isEditableTarget } from '../util/dom.ts';

const POLL_MS = 250;

export function createDebugOverlay(ed: Editor): HTMLElement {
  const text = h('pre');
  const stress = (n: number) => {
    const b = h('button', { type: 'button' }, [`+${n / 1000}k`]);
    b.addEventListener('click', () => { ed.addStressObjects(n); ed.zoomToFit(); });
    return b;
  };
  const clear = h('button', { type: 'button' }, ['Clear']);
  clear.addEventListener('click', () => ed.clearBoard());
  const el = h('div', { class: 'debug', hidden: '' }, [text, h('div', { class: 'debug-actions' }, [stress(1000), stress(10000), clear])]);

  let timer = 0;
  let lastFrames = 0;
  let lastTime = 0;

  const update = () => {
    const s = ed.renderer.stats;
    const now = performance.now();
    const fps = ((s.frames - lastFrames) * 1000) / (now - lastTime || 1);
    lastFrames = s.frames;
    lastTime = now;
    const [undo, redo] = ed.history.sizes;
    const cam = ed.camera;
    const tc = ed.textCache.stats;
    const sorted = [...s.recent].sort((a, b) => a - b);
    const p95 = sorted.length ? sorted[Math.floor(sorted.length * 0.95)]! : 0;
    text.textContent = [
      `fps        ${fps.toFixed(0).padStart(4)}   (frames drawn/s; 0 when idle)`,
      `frame ms   ${s.lastFrameMs.toFixed(2).padStart(7)}   max ${s.maxFrameMs.toFixed(2)}   p95 ${p95.toFixed(2)}`,
      `text cache ${tc.entries} rasters   ${(tc.area / 1e6).toFixed(1)} Mpx   ${ed.layouts.size} layouts   pending ${s.textPending}`,
      `objects    ${s.total}   drawn ${s.drawn}   culled ${s.total - s.drawn}`,
      `active     ${ed.renderer.activeIds.size}   selected ${ed.selection.size}`,
      `zoom       ${cam.zoom.toFixed(3)}   at ${cam.x.toFixed(0)}, ${cam.y.toFixed(0)}`,
      `history    ${undo} undo / ${redo} redo`,
      `images     ${ed.images.stats.ready} loaded   ${ed.images.stats.loading} loading`,
      ...(ed.syncInfo ? [(() => { const s = ed.syncInfo!(); return `sync       seq ${s.seq}   pending ops ${s.pending}   ${s.status}`; })()] : []),
    ].join('\n');
    s.maxFrameMs = 0;
  };

  const toggle = () => {
    el.hidden = !el.hidden;
    clearInterval(timer);
    if (el.hidden) return;
    lastFrames = ed.renderer.stats.frames;
    lastTime = performance.now();
    update();
    timer = window.setInterval(update, POLL_MS);
  };

  window.addEventListener('keydown', (e) => {
    if (e.key === '`' && !isEditableTarget(e.target) && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      toggle();
    }
  });
  return el;
}
